import {Component, EventEmitter, inject, Input, OnInit, Output} from '@angular/core';
import {CommonModule} from '@angular/common';
import {
    AuthService,
    DataSources,
    EmployeesClient, PermissionType,
    SecurityPermissionsDto,
    UpdateSecurityPermissionsCommand,
    UserStatus
} from '@services';
import notify from 'devextreme/ui/notify';
import {DxSwitchModule, DxTagBoxModule} from 'devextreme-angular';
import {Observable, of, throwError} from 'rxjs';
import {catchError} from 'rxjs/operators';
import EnumUtils from '../../../../shared/enum-utils';

@Component({
    selector: 'app-security-settings',
    templateUrl: './security-settings.component.html',
    standalone: true,
    imports: [
        CommonModule,
        DxSwitchModule,
        DxTagBoxModule
    ]
})
export class SecuritySettingsComponent implements OnInit {
    private employeesClient = inject(EmployeesClient);
    private dataSources = inject(DataSources);
    protected authService = inject(AuthService);

    @Input() userId: string;
    @Input() forceReadOnly = false;
    @Output() isDataChanged = new EventEmitter<boolean>();
    @Output() rolesChanged = new EventEmitter<string[]>();

    protected rolesDataSource = this.dataSources.roleOptions();
    securityData: SecurityPermissionsDto;
    isLoading = false;

    ngOnInit(): void {
        this.loadData();
    }

    loadData() {
        if (!this.userId) return;
        this.isLoading = true;
        this.employeesClient.getSecurityPermissions(this.userId).subscribe({
            next: (data) => {
                this.securityData = data;
                this.rolesChanged.emit(this.securityData.roles);
                this.isLoading = false;
            },
            error: () => {
                this.isLoading = false;
                notify('Failed to load security settings', 'error', 3000);
            }
        });
    }

    public save(): Observable<void> {
        if (this.isReadOnly) return of(void 0);
        if (!this.securityData) return of();

        const command = new UpdateSecurityPermissionsCommand({
            userId: this.userId,
            notifyByEmail: this.securityData.notifyByEmail,
            notifyByPhone: this.securityData.notifyByPhone,
            twoFactorAuthentication: this.securityData.twoFactorAuthentication,
            roles: this.securityData.roles
        });

        return this.employeesClient.updateSecurityPermissions(command).pipe(
            catchError((err) => {
                const errors = err?.response?.errors || [{message: 'Failed to update security settings'}];
                errors.forEach((errMsg: any) => {
                    notify({
                        message: errMsg.message || errMsg,
                        type: "error",
                        displayTime: 5000,
                    }, {
                        position: "bottom right",
                        direction: "up-push"
                    });
                });
                return throwError(() => err);
            })
        );
    }

    onFieldChange() {
        this.isDataChanged.emit(true);
    }

    onRolesChange() {
        this.isDataChanged.emit(true);
        if (this.securityData && this.securityData.roles) {
            this.rolesChanged.emit(this.securityData.roles);
        }
    }

    protected getStatusBadgeClass(status: UserStatus): string {
        return EnumUtils.getUserStatusBadgeClass(status);
    }

    get isReadOnly(): boolean {
        return this.forceReadOnly || !this.authService.canEditProfileSection(this.userId, PermissionType.UserSecurityAndPermissionsOwn, PermissionType.UserSecurityAndPermissionsOthers);
    }
}
